import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Filter as FilterIcon, X } from 'lucide-react';
import axiosInstance from 'shared/services/api/axiosInstance';
import FilterPopup from 'shared/components/filters/FilterPopup';
import PropertyPickerPopover from 'shared/components/filters/PropertyPickerPopover';
import InlineFilterChip from 'shared/components/filters/InlineFilterChip';
import { defaultOperatorForType } from 'shared/components/filters/FilterValueEditor';
import buildSpanModularFiltersUtil from 'shared/utils/filters/buildSpanModularFilters';
import { SpanKindIcon } from 'shared/components/spans/KindTag';
import {
  AddControlButton,
  QUERY_PANEL_CLASS,
  QUERY_SECTION_BODY_CLASS,
  QUERY_SECTION_DIVIDER_CLASS,
  QUERY_SECTION_HEADER_CLASS,
  QUERY_SECTION_LABEL_CLASS,
} from 'shared/components/filters/queryBuilderChrome';
import EvalChips from 'shared/components/filters/evals/EvalChips';

let _uid = 0;
const newId = () => `t${++_uid}-${Math.random().toString(36).slice(2, 7)}`;

/* ------------------------------------------------------------------ */
/* TraceRow — visual analog of AgentRow on the Runs builder.           */
/* ------------------------------------------------------------------ */
function TraceRow({
  trace,
  scopedFilters,
  teamId,
  onAddFilter,
  onUpdateFilter,
  onRemoveTrace,
  onRemoveFilter,
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const filterBtnRef = useRef(null);
  const myFilters = scopedFilters.filter((f) => f.traceId === trace.id);

  const handlePick = (picked) => {
    const op = defaultOperatorForType(picked.dataType);
    const id = newId();
    onAddFilter({
      ...picked,
      operator: op,
      value: picked.dataType === 'string' && op === 'is' ? [] : '',
      traceId: trace.id,
      traceName: trace.traceName,
      id,
      _justAdded: true,
    });
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {/* One tag, like the agent tag on Runs: the span kind's glyph, the
            name, a filter for it, and remove. */}
        <div className="inline-flex h-8 items-center gap-1 rounded-lg border border-[color:var(--border-default)] bg-surface pl-2.5 pr-1">
          <SpanKindIcon kind={trace.kind || 'span'} size={14} />
          <span className="ml-0.5 mr-1 text-[13px] font-medium text-[color:var(--text-primary)]">{trace.traceName}</span>
          <button
            ref={filterBtnRef}
            type="button"
            onClick={() => setPickerOpen(true)}
            className="flex h-6 w-6 items-center justify-center rounded-md text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
            title="Filter by this trace"
            aria-label="Filter by this trace"
          >
            <FilterIcon size={13} />
          </button>
          <button
            type="button"
            onClick={onRemoveTrace}
            className="flex h-6 w-6 items-center justify-center rounded-md text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
            title="Remove trace"
            aria-label="Remove trace"
          >
            <X size={13} />
          </button>
        </div>
      </div>

      {myFilters.length > 0 ? (
        <div className="space-y-2 pl-3">
          {myFilters.map((f, idx) => (
            <InlineFilterChip
              key={f.id}
              filter={f}
              isFirst={idx === 0}
              teamId={teamId}
              onUpdate={(updates) => onUpdateFilter(f.id, updates)}
              onRemove={() => onRemoveFilter(f.id)}
            />
          ))}
        </div>
      ) : null}

      {/* Reuse the Runs property picker: most fixed properties (status,
          model, tool_name, distinct_id, etc.) overlap with span columns.
          Properties that don't exist on agent_spans (e.g. span_count,
          total_cost) just produce a dropped chip server-side. */}
      <PropertyPickerPopover
        entity="span"
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        anchorRef={filterBtnRef}
        teamId={teamId}
        scope="global"
        onPick={handlePick}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* SpansQueryBuilder — span-level twin of AgentRunsQueryBuilder.       */
/* "Agents" section is replaced by "Trace" (pick by span name).        */
/* ------------------------------------------------------------------ */
export default function SpansQueryBuilder({
  evalLines = [],
  onEvalLinesChange = null,
  teamId,
  selectedTraces,
  onSelectedTracesChange,
  traceScopedFilters,
  onTraceScopedFiltersChange,
  globalFilters,
  onGlobalFiltersChange,
  shown = true,
}) {
  // Trace picker state.
  const [tracePickerOpen, setTracePickerOpen] = useState(false);
  const [traceList, setTraceList] = useState([]);
  const [traceLoading, setTraceLoading] = useState(false);
  const addTraceBtnRef = useRef(null);

  // Global filter property picker state.
  const [globalPickerOpen, setGlobalPickerOpen] = useState(false);
  const addGlobalFilterBtnRef = useRef(null);

  useEffect(() => {
    if (!tracePickerOpen || !teamId) return undefined;
    const ac = new AbortController();
    setTraceLoading(true);
    axiosInstance
      .get('/agent-spans/names', { params: { team_id: teamId }, signal: ac.signal })
      .then((res) => setTraceList(res.data?.traces || []))
      .catch(() => setTraceList([]))
      .finally(() => setTraceLoading(false));
    return () => ac.abort();
  }, [tracePickerOpen, teamId]);

  const availableTraces = useMemo(
    () =>
      (traceList || [])
        .filter((t) => !selectedTraces.find((s) => s.traceName === t.name))
        .map((t) => ({
          id: t.name,
          eventName: t.name,
          displayName: t.name,
          description:
            `${(t.span_count || 0).toLocaleString()} span${t.span_count === 1 ? '' : 's'}` +
            (t.kind ? ` · ${t.kind}` : '') +
            (t.last_seen_at ? ` · last seen ${new Date(t.last_seen_at).toLocaleDateString()}` : ''),
          eventType: t.kind || 'trace',
          tags: t.kind ? [t.kind] : [],
          // Stash kind for the chip header.
          _kind: t.kind || null,
        })),
    [traceList, selectedTraces],
  );

  const handleTraceSelect = (event) => {
    const picked = (traceList || []).find((t) => t.name === event.eventName);
    onSelectedTracesChange([
      ...selectedTraces,
      { id: newId(), traceName: event.eventName, kind: picked?.kind || null },
    ]);
    setTracePickerOpen(false);
  };

  const removeTrace = (id) => {
    const removed = selectedTraces.find((t) => t.id === id);
    onSelectedTracesChange(selectedTraces.filter((t) => t.id !== id));
    if (removed) {
      onTraceScopedFiltersChange(
        traceScopedFilters.filter((f) => f.traceName !== removed.traceName),
      );
    }
  };

  const addTraceScopedFilter = (filter) => {
    onTraceScopedFiltersChange((prev) => [...prev, filter]);
  };
  const updateTraceScopedFilter = (id, updates) => {
    onTraceScopedFiltersChange((prev) =>
      prev.map((f) => (f.id === id ? { ...f, ...updates } : f)),
    );
  };
  const removeTraceScopedFilter = (id) => {
    onTraceScopedFiltersChange((prev) => prev.filter((f) => f.id !== id));
  };

  const handleGlobalPick = (picked) => {
    const op = defaultOperatorForType(picked.dataType);
    const id = newId();
    const nextFilter = {
      ...picked,
      operator: op,
      value: picked.dataType === 'string' && op === 'is' ? [] : '',
      id,
      _justAdded: true,
    };
    onGlobalFiltersChange((prev) => [...prev, nextFilter]);
  };
  const updateGlobalFilter = (id, updates) => {
    onGlobalFiltersChange((prev) =>
      prev.map((f) => (f.id === id ? { ...f, ...updates } : f)),
    );
  };
  const removeGlobalFilter = (id) => {
    onGlobalFiltersChange((prev) => prev.filter((f) => f.id !== id));
  };

  if (!shown) return null;

  return (
    <div className="flex flex-col gap-2" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className={QUERY_PANEL_CLASS}>
        {/* TRACE section (replaces "AGENTS" on the Runs builder) */}
        <div className="bg-transparent">
          <div className={QUERY_SECTION_HEADER_CLASS}>
            <h3 className={QUERY_SECTION_LABEL_CLASS}>TRACE</h3>
          </div>

          <div className={QUERY_SECTION_BODY_CLASS}>
            {selectedTraces.map((t) => (
              <TraceRow
                key={t.id}
                trace={t}
                teamId={teamId}
                scopedFilters={traceScopedFilters}
                onAddFilter={addTraceScopedFilter}
                onUpdateFilter={updateTraceScopedFilter}
                onRemoveTrace={() => removeTrace(t.id)}
                onRemoveFilter={removeTraceScopedFilter}
              />
            ))}

            <div>
              <AddControlButton buttonRef={addTraceBtnRef} onClick={() => setTracePickerOpen(true)} />
            </div>
          </div>
        </div>

        <div className={QUERY_SECTION_DIVIDER_CLASS} />

        {/* FILTERS section: identical to the Runs builder. */}
        <div className="bg-transparent">
          <div className={QUERY_SECTION_HEADER_CLASS}>
            <h3 className={QUERY_SECTION_LABEL_CLASS}>FILTERS</h3>
          </div>

          <div className={QUERY_SECTION_BODY_CLASS}>
            {globalFilters.map((f, idx) => (
              <InlineFilterChip
                key={f.id}
                filter={f}
                isFirst={idx === 0}
                teamId={teamId}
                onUpdate={(updates) => updateGlobalFilter(f.id, updates)}
                onRemove={() => removeGlobalFilter(f.id)}
              />
            ))}

            {/* Eval conditions are filters too: picked from the Evals tab, shown as chips here. */}
            {onEvalLinesChange ? <EvalChips teamId={teamId} lines={evalLines} onChange={onEvalLinesChange} after={globalFilters.length} /> : null}

            <div>
              <AddControlButton buttonRef={addGlobalFilterBtnRef} onClick={() => setGlobalPickerOpen(true)} />
            </div>
          </div>
        </div>

      </div>

      {/* Trace selection popup: same FilterPopup chrome as the Runs page. */}
      <FilterPopup
        isOpen={tracePickerOpen}
        onClose={() => setTracePickerOpen(false)}
        triggerRef={addTraceBtnRef}
        navItems={[{ id: 'all', label: 'Trace', NavIcon: TraceNavIcon }]}
        filterConfigs={{ all: { fields: [], operators: {}, defaultOperators: {} } }}
        filters={[]}
        onFiltersChange={() => {}}
        getFieldOptions={() => []}
        onEventSelect={handleTraceSelect}
        availableEvents={availableTraces}
        loadingEvents={traceLoading}
        searchPlaceholder="Search spans…"
        itemNoun="spans"
      />

      {/* Global filter property picker: reuses the runs catalog. */}
      <PropertyPickerPopover
        entity="span"
        open={globalPickerOpen}
        onClose={() => setGlobalPickerOpen(false)}
        anchorRef={addGlobalFilterBtnRef}
        teamId={teamId}
        scope="global"
        onPick={handleGlobalPick}
        onPickEval={onEvalLinesChange ? (line) => onEvalLinesChange([...(evalLines || []), line]) : null}
      />
    </div>
  );
}

function TraceNavIcon({ size = 16, className = '', style = {} }) {
  return <span className={`inline-flex ${className}`} style={style}><SpanKindIcon kind="span" size={size} color="currentColor" /></span>;
}

export const buildSpanModularFilters = buildSpanModularFiltersUtil;